import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import collect from 'collect.js';
import { BasketLineItem, BasketLineItemModifier, StoreInterface } from '../store/types';
import ReduxActions from '../store/ReduxActions';
import { actionCreators } from '../store/actions';
import { EntryType } from '../services/exports/Constants';
import MenuResource from '../services/resources/MenuResource';
import useMenu from '../hooks/menu/useMenu';
import { MenuInterface, Modifier, Offer, ProductInterface } from '../services/exports/Interfaces';
import useCompanyHours from '../hooks/availability/useCompanyHours';
import useAnalytics from '../hooks/analytics/useAnalytics';
import useDiscounts from '../hooks/order/useDiscounts';
import useRewardAccount from '../hooks/loyalty/useRewardAccount';
import toFlatArray from '../utils/to-flat.util';
import { selectCompanyId, selectWorkSchedule } from '../store/slices/InitialDataSlice';
import { CompiledModifier, CompiledProduct, WithActualPrice } from './MenuProvider';
import useGetCompanyMenusApi from '../hooks/api/companies/useGetCompanyMenusApi';
import useOffersStuff from '../features/offers/hooks/useOffersStuff';
import useGetBasketOfferHandlerByKind from '../features/offers/hooks/get-offer-handler/useGetBasketOfferHandlerByKind';

interface Props {
  children: any;
}

export interface BasketContextInterface {
  data: BasketLineItem[];
  items: CompleteBasketItem[];
  originalItems: CompleteBasketItem[];
  isValid: boolean;
  freeItem: null | CompleteBasketItem;
  quantity: number;
  subtotal: number;
  total: number;
  basketItemsCount: number;
  compileItem: (item: BasketLineItem, originalIndex: number, offers?: Offer[]) => CompleteBasketItem;
  addItem: (item: BasketLineItem) => void;
  addItems: (items: BasketLineItem[]) => void;
  updateItem: (basketItemIndex: number, item: BasketLineItem) => void;
  incrementItem: (index: number) => void;
  decrementItem: (index: number) => void;
  clearBasket: () => void;
  refreshBasket: () => void;
  formatForBackend: () => BasketLineItem[];
  formatItemForBackend: (data: BasketLineItem) => BasketLineItem;
  getProductIds: () => number[];
  compileModifier(modifier: BasketLineItemModifier, isFreeItem?: boolean): CompleteBasketItemModifier;
}

export type CompleteBasketItem = Omit<BasketLineItem, 'modifiers'> & {
  entry: WithActualPrice<ProductInterface>;
  menu: MenuInterface;
  original_price: number;
  actual_price: number;
  is_available: boolean;
  is_sold_out: boolean;
  original_index: number;

  modifiers: CompleteBasketItemModifier[];
  badges: string[];
};

export type CompleteBasketItemModifier = BasketLineItemModifier & {
  entry: WithActualPrice<Modifier | ProductInterface>;
  original_price: number;
  actual_price: number;
};

export const BasketContext = React.createContext<BasketContextInterface>(null);

export default function BasketProvider(props: Props) {
  const { children } = props;
  const companyId = useSelector(selectCompanyId);
  const workSchedule = useSelector(selectWorkSchedule);
  const { data: menusData } = useGetCompanyMenusApi();
  const { cached_order } = useSelector((state: StoreInterface) => state.order);
  const { lineItems } = useSelector((store: StoreInterface) => store.basket);
  const data: BasketLineItem[] = lineItems[companyId] ?? [];
  const { findFreeProduct, compileProduct, applyOrderMethodPrices } = useMenu();
  const { trackAddToBasket, trackRemoveFromBasket } = useAnalytics();
  const { orderTimeAvailable } = useCompanyHours();
  const { calculateDiscount } = useDiscounts();
  const { hasFreeItem } = useRewardAccount();
  const { appliedOffers } = useOffersStuff();
  const getOfferHandler = useGetBasketOfferHandlerByKind();

  const refreshBasketInterval = 30000;
  const [refreshBasketTrigger, setRefreshBasketTrigger] = useState(false);

  useEffect(() => {
    const id = setInterval(() => refreshBasket(), refreshBasketInterval);

    return () => clearInterval(id);
  }, []);

  const formatItemForBackend = (data: BasketLineItem): BasketLineItem => {
    return {
      product_id: data.product_id,
      quantity: data.quantity,
      is_free: data.is_free,
      note: data.note,
      modifiers: data.modifiers,
    };
  };

  const formatForBackend = (): BasketLineItem[] => {
    return items?.map((item) => formatItemForBackend(item));
  };

  const applyPrices = (
    item: Omit<CompleteBasketItem, 'original_price' | 'actual_price' | 'is_sold_out'>,
  ): CompleteBasketItem => {
    if (!item.entry) {
      return {
        ...item,
        is_sold_out: true,
        original_price: 0,
        actual_price: 0,
      };
    }

    const isSoldOut =
      !item.entry.is_available ||
      !!collect(toFlatArray(item.modifiers, 'modifiers')).whereNotNull().firstWhere('entry.is_available', false);

    return {
      ...item,
      is_sold_out: isSoldOut,
      original_price:
        (item.entry.base_price +
          +collect(item.modifiers)
            .whereNotNull()
            .sum((modifier: CompleteBasketItemModifier) => modifier.original_price * modifier.quantity)) *
        item.quantity,
      actual_price:
        (item.entry.actual_price +
          +collect(item.modifiers)
            .whereNotNull()
            .sum((modifier: CompleteBasketItemModifier) => modifier.actual_price * modifier.quantity)) *
        item.quantity,
    };
  };

  const getSortingPosition = (
    entry: CompiledProduct | CompiledModifier,
    lineItemModifier: CompleteBasketItemModifier,
  ) => {
    const groupIndex = entry?.modifier_group_ids?.indexOf(lineItemModifier.modifier_group_id);

    return (
      groupIndex * 1000 +
      collect(entry?.modifier_groups[groupIndex]?.modifier_ids ?? []).search(
        (item) => item.id === lineItemModifier.modifier_id && item.type === lineItemModifier.modifier_type,
      )
    );
  };

  const pureCompileItem = (item: BasketLineItem, originalIndex: number): CompleteBasketItem => {
    const isFree = item.is_free && hasFreeItem;
    const product = isFree ? findFreeProduct(item.product_id) : compileProduct(item.product_id);
    const categories = collect(product?.category_ids).map((id) => menusData?.categories[id]);
    const menus = collect(menusData?.menus).whereIn('id', categories.pluck('menu_ids').flatten().toArray());
    const menu = menus.first((menu) => new MenuResource(menu, workSchedule).isActive(cached_order?.scheduled_for));
    const isAvailable = !!menu;

    const modifiers = collect(item.modifiers)
      .map((modifier) => compileModifier(modifier, item.is_free))
      .whereNotNull('modifier')
      .sort((a, b) => getSortingPosition(product, a) - getSortingPosition(product, b));

    return applyPrices({
      ...item,
      entry: product,
      menu: menu ?? menus.first(),
      modifiers: modifiers.toArray(),
      is_available: orderTimeAvailable ? isAvailable : true,
      original_index: originalIndex,
      badges: [],
    });
  };

  const compileItem = (item: BasketLineItem, originalIndex: number, offers?: Offer[]): CompleteBasketItem => {
    const completeItem = pureCompileItem(item, originalIndex);
    const eligibleOffers = collect(appliedOffers)
      .when(Boolean(offers?.length), (collection) => collection.merge(offers))
      .unique('id')
      .toArray<Offer>();

    if (!eligibleOffers?.length) {
      return completeItem;
    }

    const offerHandlers = eligibleOffers
      .map((appliedOffer) => getOfferHandler(appliedOffer, [...completeItems, completeItem]))
      .filter((appliedOffer) => appliedOffer?.apply);

    if (!offerHandlers?.length) {
      return completeItem;
    }

    return offerHandlers.reduce((result, handler) => handler.apply(result), completeItem);
  };

  const getPriceBy = (modifier: BasketLineItemModifier, by: string): number => {
    const entry =
      modifier.modifier_type === EntryType.Product
        ? menusData?.products?.[modifier.modifier_id]
        : menusData?.modifiers?.[modifier.modifier_id];
    const data = applyOrderMethodPrices(entry ?? null);

    let result: number = data[by] ?? 0;

    modifier?.modifiers?.forEach((nestedModifier) => {
      result += getPriceBy(nestedModifier, by) * nestedModifier.quantity;
    });

    return result;
  };

  const compileModifier = (modifier: BasketLineItemModifier, isFreeItem = false): CompleteBasketItemModifier => {
    const entry =
      modifier.modifier_type === EntryType.Product
        ? menusData?.products?.[modifier.modifier_id]
        : menusData?.modifiers?.[modifier.modifier_id];
    const data = applyOrderMethodPrices(entry ?? null);
    const isFree = isFreeItem && hasFreeItem && data?.free_eligible;

    const originalPrice = getPriceBy(modifier, 'base_price');
    const actualPrice = getPriceBy(modifier, 'actual_price');
    const modifiers = modifier.modifiers?.length
      ? modifier.modifiers.map((nestedModifier) => compileModifier(nestedModifier, isFree))
      : [];

    return {
      ...modifier,
      entry: data,
      original_price: originalPrice,
      actual_price: isFree ? 0 : actualPrice,
      modifiers,
    };
  };

  const completeItems = useMemo<CompleteBasketItem[]>(
    () =>
      collect(data)
        .map((item, index) => pureCompileItem(item, index))
        .filter((item) => !item.is_sold_out)
        .filter((item) => !!item.entry && !!item.menu)
        .filter((item) => !item.modifiers.length || Boolean(item.modifiers[0]?.modifier_type))
        .whereNotNull('product')
        .toArray(),
    [
      data,
      menusData?.products,
      menusData?.modifiers,
      menusData?.categories,
      menusData?.menus,
      cached_order?.scheduled_for,
      cached_order?.coupon_type,
      cached_order?.method,
      refreshBasketTrigger,
    ],
  );
  const items = useMemo(() => {
    if (!appliedOffers?.length) {
      return completeItems;
    }

    return appliedOffers.reduce((result, appliedOffer) => {
      const offerHandler = getOfferHandler(appliedOffer, result);

      if (!offerHandler?.apply) {
        return result;
      }

      return result.map(offerHandler.apply);
    }, completeItems);
  }, [completeItems, appliedOffers]);

  const searchItem = useCallback(
    (item: BasketLineItem): number => {
      if (!companyId || items.length === 0 || item.is_free) {
        return null;
      }

      const modifiers = collect(toFlatArray(item.modifiers, 'modifiers'));

      return collect(items).search(
        (lineItem) =>
          !lineItem.is_free &&
          item.product_id === lineItem.product_id &&
          item.note === lineItem.note &&
          modifiers
            .sortBy(
              (modifier) =>
                `${modifier.modifier_group_id}-${modifier.modifier_id}-${modifier.modifier_type}-${modifier.quantity}`,
            )
            .reduce(
              (carry, modifier) =>
                carry +
                `${modifier.modifier_group_id}-${modifier.modifier_id}-${modifier.modifier_type}-${modifier.quantity}`,
              '',
            ) ===
            collect(lineItem.modifiers)
              .sortBy(
                (modifier) =>
                  `${modifier.modifier_group_id}-${modifier.modifier_id}-${modifier.modifier_type}-${modifier.quantity}`,
              )
              .reduce(
                (carry, modifier) =>
                  carry +
                  `${modifier.modifier_group_id}-${modifier.modifier_id}-${modifier.modifier_type}-${modifier.quantity}`,
                '',
              ),
      );
    },
    [items, companyId],
  );

  const addItem = (item: BasketLineItem) => {
    if (!companyId) {
      return;
    }

    const index = searchItem(item);

    if (typeof index === 'number' && index >= 0) {
      return incrementItem(index);
    }

    ReduxActions.dispatch(actionCreators.basket.addItem({ company_id: companyId, item }));
    trackAddToBasket(pureCompileItem(item, 0));
  };

  const addItems = (items: BasketLineItem[]) => {
    if (!companyId) {
      return;
    }

    ReduxActions.dispatch(actionCreators.basket.addItems({ company_id: companyId, items }));
    Promise.all(items.map((item) => trackAddToBasket(pureCompileItem(item, 0))));
  };

  const updateItem = (basketItemIndex: number, updatedBasketItem: BasketLineItem): void => {
    const item = items[basketItemIndex];

    ReduxActions.dispatch(
      actionCreators.basket.updateItem({
        company_id: companyId,
        index: item.original_index,
        item: updatedBasketItem,
      }),
    );

    trackAddToBasket(pureCompileItem(item, item.original_index));
  };

  const incrementItem = (index: number): void => {
    if (!companyId) {
      return;
    }

    const item = items[index];

    ReduxActions.dispatch(
      actionCreators.basket.updateItem({
        company_id: companyId,
        index: item.original_index,
        item: { quantity: item.quantity + 1 },
      }),
    );

    trackAddToBasket({
      ...pureCompileItem({ ...item, quantity: item.quantity + 1 }, index),
      quantity: item.quantity + 1,
    });
  };

  const decrementItem = (index: number): void => {
    if (!companyId) {
      return;
    }

    const item = items[index];

    item.quantity === 1
      ? ReduxActions.dispatch(actionCreators.basket.removeItem({ company_id: companyId, index }))
      : ReduxActions.dispatch(
          actionCreators.basket.updateItem({
            company_id: companyId,
            index: item.original_index,
            item: { quantity: item.quantity - 1 },
          }),
        );

    trackRemoveFromBasket({
      ...pureCompileItem({ ...item, quantity: item.quantity - 1 }, index),
      quantity: item.quantity - 1,
    });
  };

  const basketItemsCount = useMemo(() => items?.reduce((acc, item) => acc + item.quantity, 0), [items]);

  const clearBasket = () => companyId && ReduxActions.dispatch(actionCreators.basket.clear({ company_id: companyId }));

  const refreshBasket = () => setRefreshBasketTrigger((prev) => !prev);

  const getProductIds = (): number[] =>
    collect(items).pluck('product_id').merge(collect(items).pluck('children.product_id')).toArray();

  const isValid = useMemo(() => !collect(items).first((item) => item.is_sold_out), [items]);

  const freeItem = useMemo<CompleteBasketItem | null>(() => collect(items).firstWhere('is_free', true), [items]);

  const quantity = useMemo<number>(() => +collect(items).sum('quantity'), [items]);

  const subtotal = useMemo<number>(() => +collect(completeItems).sum('original_price'), [completeItems]);
  const actualSubtotal = useMemo<number>(() => +collect(completeItems).sum('actual_price'), [completeItems]);

  const discount = useMemo<number>(() => {
    const discount = calculateDiscount(actualSubtotal);

    if (!appliedOffers?.length) {
      return discount;
    }

    const itemsDiscount = actualSubtotal - +collect(items).sum('actual_price');

    const offerHandlers = appliedOffers
      .map((appliedOffer) => getOfferHandler(appliedOffer, completeItems))
      .filter((handler) => !handler?.apply);

    return offerHandlers.reduce((result, handler) => result + handler.profit, discount + itemsDiscount);
  }, [cached_order?.coupon_type, cached_order?.coupon_details, actualSubtotal, appliedOffers, completeItems, items]);

  const total = useMemo<number>(() => Math.max(0, actualSubtotal - discount), [actualSubtotal, discount]);

  const context: BasketContextInterface = {
    data,
    items,
    originalItems: completeItems,
    isValid,
    freeItem,
    quantity,
    subtotal,
    total,
    basketItemsCount,
    compileItem,
    addItem,
    incrementItem,
    decrementItem,
    clearBasket,
    refreshBasket,
    formatForBackend,
    formatItemForBackend,
    getProductIds,
    compileModifier,
    updateItem,
    addItems,
  };

  return <BasketContext.Provider value={context}>{children}</BasketContext.Provider>;
}
